import React, { useEffect } from "react";
import { useErrorBoundary } from "react-error-boundary";
import type { ResponseValues } from "axios-hooks";
import cookie from "js-cookie";
import { isNil } from "ramda";

import { NavigateButton } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { Layout } from "affinityUI/structures/layout";
import { useRequest } from "lib/api";
import { IdleHandler } from "lib/inactivityHandler/idleHandler";

import { PasswordForm } from "./passwordForm";

const isPasswordTokenValid = (
  passwordTokenValidation: ResponseValues<{ status: string }, unknown, { detail: string }>
): Maybe<boolean> => {
  if (passwordTokenValidation?.loading) return null;
  if (!isNil(passwordTokenValidation?.error) && passwordTokenValidation?.error?.response?.data?.detail === "Not found.")
    return false;
  if (!isNil(passwordTokenValidation?.data) && passwordTokenValidation?.data?.status === "OK") return true;
  return null;
};

const handleIdle = () => {
  window.location.reload();
};

export const SetPasswordPage: React.FC = () => {
  const { showBoundary: onError } = useErrorBoundary();
  const [passwordTokenValidationRes, validatePasswordToken] = useRequest(
    {
      url: "/password_reset/validate_token/",
      method: "POST",
    },
    {
      manual: true,
    }
  );

  const passwordToken = cookie.get("password_reset_token");
  const passwordTokenIsValid = isPasswordTokenValid(passwordTokenValidationRes);
  if (passwordTokenIsValid === null && passwordTokenValidationRes.error) {
    onError(passwordTokenValidationRes.error);
  }

  useEffect(() => {
    if (!isNil(passwordToken)) {
      validatePasswordToken({ data: { token: passwordToken } });
    }
  }, [passwordToken, validatePasswordToken]);

  return (
    <Layout title="New Password" publicLayout loading={passwordTokenValidationRes.loading}>
      {passwordTokenIsValid && !isNil(passwordToken) && (
        <>
          <IdleHandler timeout={5 * 60 * 1000} onIdle={handleIdle} />
          <PasswordForm passwordToken={passwordToken} />
        </>
      )}
      {isNil(passwordToken) && (
        <Grid>
          <div>
            <p>Password form is timed out. It likely happened because of user inactivity.</p>
            <p>Please reopen link in received email to try again.</p>
          </div>
        </Grid>
      )}
      {passwordTokenIsValid === false && (
        <Grid>
          <div>There is a problem with set password link.</div>
          <NavigateButton to={"../../recover"}>Resend password recovery email</NavigateButton>
        </Grid>
      )}
    </Layout>
  );
};
